import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { BarcodeFigure } from "@/components/guides/BarcodeFigure";
import { Callout } from "@/components/guides/Callout";
import { TryTool } from "@/components/guides/TryTool";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["barcode-formats"];

const TOC = [
  { id: "the-short-answer", label: "The short answer" },
  { id: "at-a-glance", label: "All formats at a glance" },
  { id: "one-dimensional", label: "1D formats" },
  { id: "two-dimensional", label: "2D formats" },
  { id: "how-to-choose", label: "How to choose" },
  { id: "identify-unknown", label: "Identifying an unknown barcode" },
  { id: "common-mistakes", label: "Common mistakes" },
  { id: "quick-decision", label: "Quick decision guide" },
  { id: "how-many-types", label: "How many types of barcodes are there?" },
  { id: "types-by-industry", label: "Barcode types and uses by industry" },
  { id: "qr-barcode-types", label: "QR barcode types" },
  { id: "references", label: "References" },
];

const FAQ = [
  {
    q: "How do I tell what barcode format I'm looking at?",
    a: "Count the digits and look at the shape. Two stacked groups of parallel bars with 12–13 digits → UPC-A or EAN-13. A wider Code 128 has variable-width bars and no digit grouping. A square matrix of dots is QR or Data Matrix; a tall narrow stack of rows is PDF417. Our Barcode Type Detector identifies a pasted UPC, EAN, GTIN-14 or ISBN number automatically, and the barcode reader names the symbology of any scanned code.",
  },
  {
    q: "Which barcode format should I print on consumer packaging?",
    a: "EAN-13 (or UPC-A in North America) for the GTIN, plus — increasingly — a 2D code (QR or GS1 Data Matrix) for batch, expiry and a Digital Link URL. That dual-carrier approach is exactly what GS1 Sunrise 2027 is preparing the industry for.",
  },
  {
    q: "Can I Encode My Website URL in a Barcode?",
    a: "Yes — that's exactly what QR codes are designed for. Don't try to fit a URL into a 1D barcode; capacity is far too low. For GS1-aware tracking, encode the URL as a GS1 Digital Link so retail systems can extract the GTIN from the same scan.",
  },
  {
    q: "Why Is ITF-14 Used on Cases Instead of EAN-13?",
    a: "ITF-14 (Interleaved 2 of 5, 14 digits) is more tolerant of direct-print on corrugated cardboard, where ink can spread and ruin the fine bars of an EAN-13. It encodes the GTIN-14 of the case (the consumer GTIN with an indicator digit).",
  },
  {
    q: "Are PDF417 and Aztec Still Relevant?",
    a: "Yes, in specific niches. PDF417 is mandated on the back of US driver's licences and many airline boarding passes. Aztec is heavy on European rail tickets. Neither is a good general-purpose choice today — QR and Data Matrix have won that battle.",
  },
  {
    q: "What's the Difference Between Code 128 and GS1-128?",
    a: "Same symbology, different rules. GS1-128 is Code 128 with a mandatory FNC1 character at the start that signals the payload follows GS1 Application Identifier syntax. The bars look identical; the parsing on the other end is stricter.",
  },
  {
    q: "Are QR Codes a Type of Barcode?",
    a: "Yes. QR Code is a 2D matrix barcode symbology, standardized in ISO/IEC 18004, that stores data in a grid of square modules instead of a row of bars. Phone cameras and imaging scanners read it, but a laser scanner that sweeps a single line across the label cannot.",
  },
  {
    q: "What Barcode Type Do Stores Use?",
    a: "Checkout lanes scan EAN-13 worldwide and UPC-A in the US and Canada, with EAN-8 and UPC-E on very small packs and GS1 DataBar on some fresh foods. Each of them carries a GTIN that the till looks up to find the price. Many retailers are now adding 2D barcodes, such as a QR code with a GS1 Digital Link, next to the 1D barcode as part of GS1 Sunrise 2027.",
  },
];

export const Route = createFileRoute("/barcode-guides/barcode-formats")({
  component: BarcodeFormatsGuide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function BarcodeFormatsGuide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/1d-vs-2d-barcodes",
          label: "1D vs 2D barcodes",
          desc: "The capacity and scanner-support story behind the format split below.",
        },
        {
          to: "/barcode-guides/what-is-a-gtin",
          label: "What is a GTIN?",
          desc: "The identifier that sits inside UPC-A, EAN-13, ITF-14 and ISBN.",
        },
        {
          to: "/barcode-type-detector",
          label: "Barcode Type Detector",
          desc: "Paste a printed value and see which GS1 format or ISBN it matches.",
        },
        {
          to: "/barcode-reader",
          label: "Online Barcode Reader",
          desc: "Scan a 1D or 2D barcode and see its format next to the decoded value.",
        },
      ]}
    >
      <p>
        Your team is launching a new product across UK supermarkets, US mass-market and a German
        pharmacy chain. By the end of the day someone needs an answer to a question that sounds
        simple — &quot;which barcode goes on the box?&quot; — and that answer turns out to be
        different on every channel. The consumer pack wants EAN-13. The US version wants UPC-A. The
        pharmacy version wants GS1 Data Matrix carrying batch and expiry. The outer case wants
        ITF-14. The carton-level pallet wraps a GS1-128 with an SSCC. And the marketing team would
        quite like a QR code on the front for the Digital Link.
      </p>
      <p>
        There are dozens of <strong>barcode formats</strong> in the wild, but a dozen account for
        almost every barcode you&apos;ll meet in retail, logistics, healthcare and identity
        documents. This <strong>barcode formats field guide</strong> covers what each one encodes,
        where you&apos;ll see it on packaging, paperwork or screens, and which ReadBarcode tool
        decodes or generates it. By the end of the article you should know the <strong>different types of barcodes</strong>, the difference between 1D barcode types and 2D
        ones, and how to identify an unknown barcode from nothing but the printed value.
      </p>

      <GuideArticleSection id="the-short-answer" title="The Short Answer">
        <p>
          Barcodes split into two families. <strong>1D barcodes</strong> are the parallel bars you
          see on every supermarket pack — they encode short identifiers like the GTIN that the till
          looks up to find a price. <strong>2D barcodes</strong> are the square or stacked grids you
          see on boarding passes, driver&apos;s licences, pharma vials and consumer-marketing
          posters — they encode a full payload (batch, expiry, URL, serial) inside a much smaller
          footprint with built-in error correction.
        </p>
        <p>
          Within each family a handful of symbologies dominate. On 1D side, you&apos;ll almost
          always be choosing between UPC-A, EAN-13, EAN-8, Code 128 (and its GS1-128 cousin), Code
          39 and ITF-14. On 2D side, QR Code, GS1 Data Matrix, PDF417 and Aztec cover effectively
          everything you&apos;ll meet. Niche industrial formats exist (Code 93, Codabar, MaxiCode,
          Han Xin) but if you&apos;re reading this for a packaging or logistics decision, you
          don&apos;t need them.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="at-a-glance" title="All Formats at a Glance">
        <p>
          The single reference table for the rest of the article. Capacities are practical rather
          than spec-maximum — what fits on a label and still scans reliably on real hardware.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              All common barcode formats by family, capacity, character set and where you meet them
              in practice.
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  Format
                </th>
                <th scope="col">
                  Family
                </th>
                <th scope="col">
                  Capacity
                </th>
                <th scope="col">
                  Where you see it
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">
                  UPC-A
                </th>
                <td>1D</td>
                <td>12 digits</td>
                <td>US &amp; Canadian retail GTIN</td>
              </tr>
              <tr>
                <th scope="row">
                  EAN-13
                </th>
                <td>1D</td>
                <td>13 digits</td>
                <td>Worldwide retail GTIN</td>
              </tr>
              <tr>
                <th scope="row">
                  EAN-8
                </th>
                <td>1D</td>
                <td>8 digits</td>
                <td>
                  Small retail packs (sweets, lip balm, electronics blisters)
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Code 128
                </th>
                <td>1D</td>
                <td>~80 alphanumeric</td>
                <td>
                  Shipping labels, asset tags, hospital wristbands
                </td>
              </tr>
              <tr>
                <th scope="row">
                  GS1-128
                </th>
                <td>1D (Code 128 + FNC1)</td>
                <td>Code 128 capacity, AI-structured</td>
                <td>
                  GS1 logistics labels, SSCC pallet IDs
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Code 39
                </th>
                <td>1D</td>
                <td>~30 alphanumeric (upper only)</td>
                <td>
                  Legacy automotive, defence, library catalogues
                </td>
              </tr>
              <tr>
                <th scope="row">
                  ITF-14
                </th>
                <td>1D</td>
                <td>14 digits</td>
                <td>
                  Outer cases printed direct on corrugated cardboard
                </td>
              </tr>
              <tr>
                <th scope="row">
                  ISBN (EAN-13)
                </th>
                <td>1D</td>
                <td>13 digits (978/979 prefix)</td>
                <td>Books, magazines, music</td>
              </tr>
              <tr>
                <th scope="row">
                  QR Code
                </th>
                <td>2D matrix</td>
                <td>~4,000 alphanumeric</td>
                <td>
                  Consumer marketing, payments, GS1 Digital Link
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Data Matrix
                </th>
                <td>2D matrix</td>
                <td>~2,300 alphanumeric</td>
                <td>
                  Pharma vials, surgical instruments, electronic components
                </td>
              </tr>
              <tr>
                <th scope="row">
                  PDF417
                </th>
                <td>2D stacked</td>
                <td>~1,800 characters</td>
                <td>
                  US driver&apos;s licences, airline boarding passes
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Aztec
                </th>
                <td>2D matrix</td>
                <td>~3,000 characters</td>
                <td>
                  Rail and transport tickets (Eurostar, SNCF, Deutsche Bahn)
                </td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
      </GuideArticleSection>

      <GuideArticleSection id="one-dimensional" title="1D Formats">
        <p>
          The 1D family is dominated by GS1-issued retail identifiers (UPC-A, EAN-13, EAN-8, ITF-14)
          and the alphanumeric workhorses behind them in logistics (Code 128, GS1-128, Code 39). All
          of them encode data along a single horizontal axis using bars of varying width, all of
          them carry a trailing check character (optional in Code 39), none of them have meaningful error correction.
        </p>

        <h3>UPC-A and EAN-13 — the Retail Backbone</h3>
        <p>
          UPC-A is 12 digits, EAN-13 is 13. They&apos;re the same product identifier in different
          sleeves — a UPC-A is an EAN-13 with a leading zero. Every modern point-of-sale scanner
          reads both. For the full deep-dive on why the two exist, see the <Link to="/barcode-guides/upc-vs-ean">UPC vs EAN guide</Link>.
        </p>
        <BarcodeFigure
          format="ean13"
          value="5012345678900"
          formatLabel="EAN-13"
          caption={
            <>
              EAN-13 encoding the GTIN-13 <code>5012345678900</code> — the typical consumer-pack
              barcode outside North America.
            </>
          }
        />

        <h3>EAN-8 — the Short Retail Form</h3>
        <p>
          EAN-8 is a separate 8-digit GS1 allocation for packages too small for an EAN-13. It is not
          a truncation of EAN-13 — the brand has to apply for the EAN-8 allocation specifically.
        </p>
        <BarcodeFigure
          format="ean8"
          value="96385074"
          formatLabel="EAN-8"
          caption={
            <>
              EAN-8 encoding the standalone 8-digit GTIN <code>96385074</code>, sized to fit on
              lip-balm-class packaging.
            </>
          }
        />

        <h3>ITF-14 — the Case-Level Identifier</h3>
        <p>
          ITF-14 is Interleaved 2 of 5 fixed to exactly 14 digits. The thicker bars survive
          direct-print on corrugated cardboard where the fine bars of an EAN-13 would bleed into
          each other. It encodes the GTIN-14 of the outer case — the consumer GTIN with an indicator
          digit on the front.
        </p>
        <BarcodeFigure
          format="itf14"
          value="10614141000415"
          formatLabel="ITF-14"
          caption={
            <>
              ITF-14 encoding the carton GTIN-14 <code>10614141000415</code> — leading <code>1</code> is the packaging-level indicator.
            </>
          }
        />

        <h3>Code 128 — the Alphanumeric Workhorse</h3>
        <p>
          Code 128 encodes the full 128-character ASCII range in a dense form, with three internal
          subsets (A, B, C) that the encoder picks between to keep the symbol narrow. It dominates
          shipping labels, asset stickers and hospital wristbands. The <Link to="/barcode-guides/code-128-explained">Code 128 guide</Link> walks through subsets
          and FNC1 in detail.
        </p>
        <BarcodeFigure
          format="code128"
          value="SHIP-2026-00042"
          formatLabel="Code 128"
          caption={
            <>
              Code 128 encoding the internal asset reference <code>SHIP-2026-00042</code> — a
              mixed-case alphanumeric payload typical of warehouse labels.
            </>
          }
        />

        <h3>GS1-128 — Code 128 With GS1 Syntax</h3>
        <p>
          GS1-128 is Code 128 with a mandatory FNC1 at the start, signalling that the payload
          follows GS1 Application Identifier syntax. The bars look identical to a plain Code 128,
          but parsers treat the contents as a chain of AIs separated by FNC1.
        </p>
        <BarcodeFigure
          format="gs1128"
          value="(01)07640156790009(10)ABC123(17)261231"
          formatLabel="GS1-128"
          caption={
            <>
              GS1-128 carrying GTIN-14 <code>07640156790009</code>, batch <code>ABC123</code> and
              expiry <code>2026-12-31</code> in a single 1D symbol.
            </>
          }
        />

        <h3>Code 39 — the Legacy Alphanumeric</h3>
        <p>
          Code 39 predates Code 128 and is still found in defence, automotive and library systems
          whose scanners haven&apos;t been refreshed. It only encodes uppercase letters, digits and
          a handful of symbols, and produces a symbol roughly twice as wide as Code 128 for the same
          payload.
        </p>
        <BarcodeFigure
          format="code39"
          value="ASSET-2026-00042"
          formatLabel="Code 39"
          caption={
            <>
              Code 39 encoding the uppercase reference <code>ASSET-2026-00042</code>. Note the wider
              footprint compared with the equivalent Code 128 above.
            </>
          }
        />

        <h3>ISBN — Books, Magazines, Music</h3>
        <p>
          The 13-digit barcode on the back of every book is an EAN-13 with a fixed <code>978</code> or <code>979</code> prefix, allocated globally for publishing. A library system that
          doesn&apos;t know the prefix treats it as a plain EAN-13 and works fine.
        </p>
        <BarcodeFigure
          format="ean13"
          value="9780140449143"
          formatLabel="ISBN-13 (EAN-13)"
          caption={
            <>
              ISBN-13 <code>9780140449143</code> encoded as an EAN-13 barcode — the <code>978</code> prefix marks it as a book.
            </>
          }
        />
      </GuideArticleSection>

      <GuideArticleSection id="two-dimensional" title="2D Formats">
        <p>
          The 2D family arrived later and addresses two limits of 1D barcodes: capacity and damage
          tolerance. Every 2D format encodes data in a matrix of light and dark modules with
          Reed–Solomon error correction that can repair a damaged part of the symbol (up to roughly 30% at QR Code&apos;s highest level) — the same
          maths that lets a phone scan a QR code through a coffee stain. The four formats below
          cover effectively everything in practice; the rest are industrial niches.
        </p>

        <h3>QR Code — the Consumer 2D</h3>
        <p>
          QR (Quick Response) is the format every modern phone camera reads natively. It handles
          URLs, Wi-Fi credentials, vCards, payments and — increasingly — GS1 Digital Link payloads
          that double as the retail GTIN. For Sunrise 2027, the QR with a Digital Link URL is what
          most consumer-pack brands will print alongside the 1D EAN.
        </p>
        <BarcodeFigure
          format="qrcode"
          value="https://readbarcode.com"
          formatLabel="QR Code"
          caption={
            <>
              QR Code encoding the URL <code>https://readbarcode.com</code> — the simplest 2D format
              and the only one a phone camera reads with zero setup.
            </>
          }
        />

        <h3>GS1 Data Matrix — the Pharma 2D</h3>
        <p>
          Data Matrix achieves the same density as QR in a smaller footprint, which is why GS1
          specifies it for pharmaceutical secondary packaging, medical devices and unit-dose markings.
          The GS1 variant uses the same FNC1 + Application Identifier structure as GS1-128, in a
          square symbol that fits on a vial cap.
        </p>
        <BarcodeFigure
          format="datamatrix"
          value="(01)05012345678900(10)ABC123(17)261231"
          formatLabel="GS1 Data Matrix"
          caption={
            <>
              GS1 Data Matrix carrying GTIN <code>05012345678900</code>, batch <code>ABC123</code> and expiry <code>2026-12-31</code> in a footprint a few millimetres across.
            </>
          }
        />

        <h3>PDF417 — the Document 2D</h3>
        <p>
          PDF417 is a <em>stacked</em> 2D barcode — rows of small 1D-like patterns stacked
          vertically — rather than a true matrix. It dominates documents that need a lot of data in
          a single capture: US driver&apos;s licences, airline boarding passes, shipping manifests.
          Phone cameras read it but support is patchier than QR.
        </p>
        <BarcodeFigure
          format="pdf417"
          value="M1DOE/JOHN          EABC123 LHRJFKBA  0179 226Y012A0001 100"
          formatLabel="PDF417"
          caption={
            <>
              PDF417 encoding an IATA-format boarding-pass payload — passenger name, record locator,
              segment data — across a stacked 2D symbol.
            </>
          }
        />

        <h3>Aztec — the Transit 2D</h3>
        <p>
          Aztec has a distinctive bullseye finder pattern at its centre and doesn&apos;t need a
          quiet zone, so it scans reliably from glossy phone screens with thin status bars. That
          makes it the standard for European rail and transport tickets — Eurostar, SNCF, Deutsche
          Bahn — where the symbol lives on a small screen the passenger flashes at a gate scanner.
        </p>
        <BarcodeFigure
          format="aztec"
          value="M1DOE/JOHN          EABC123 LHRJFKBA  0179 226Y012A0001 100"
          formatLabel="Aztec"
          caption={
            <>
              Aztec code encoding the same boarding-pass payload — note the bullseye finder pattern
              at the centre and the comparable footprint to Data Matrix.
            </>
          }
        />
      </GuideArticleSection>

      <GuideArticleSection id="how-to-choose" title="How to Choose">
        <p>
          The choice between formats almost always reduces to four questions: what data are you
          encoding, what hardware scans it, how much space does the label have, and which standards
          apply on the receiving end? The pragmatic answers for the common cases:
        </p>
        <ul>
          <li>
            <strong>Fixed consumer product.</strong> EAN-13 worldwide, UPC-A if you have a North
            American GS1 allocation. Add a QR with a Digital Link if you want a Sunrise-2027-ready
            consumer pack.
          </li>
          <li>
            <strong>Books and serials.</strong> ISBN-13 as an EAN-13 with <code>978</code> or <code>979</code> prefix; ISSN as EAN-13 with <code>977</code>.
          </li>
          <li>
            <strong>Outer case or pallet.</strong> ITF-14 on the case (corrugated tolerance) or
            GS1-128 on the pallet (SSCC + AI payload). Don&apos;t use EAN-13 on cardboard — the fine
            bars bleed.
          </li>
          <li>
            <strong>Internal asset / inventory label.</strong> Code 128 — densest alphanumeric
            without the GS1 overhead.
          </li>
          <li>
            <strong>Pharma / medical device.</strong> GS1 Data Matrix on the unit-dose package,
            GS1-128 on the carton, GS1 Digital Link QR if you also need a consumer-facing scan.
          </li>
          <li>
            <strong>Document or ID.</strong> PDF417 for fixed-format US documents (drivers licences,
            BCBP boarding passes); Aztec for ticket-style screens; QR for anything else.
          </li>
          <li>
            <strong>Marketing / consumer engagement.</strong> QR with a regular URL or a GS1 Digital
            Link.
          </li>
        </ul>
        <Callout variant="in-practice" title="Run dual carriers on consumer packs">
          <p>
            The trend for 2026–2028 isn&apos;t replacing 1D with 2D — it&apos;s carrying both on the
            same pack. The 1D EAN-13 stays at the till until every retailer in your channel is
            Sunrise-ready; the 2D Digital Link QR earns its place on the front of the pack for the
            phone scan. See the <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 Guide</Link> for the
            migration story.
          </p>
        </Callout>
      </GuideArticleSection>

      <GuideArticleSection id="identify-unknown" title="Identifying an Unknown Barcode">
        <p>
          Sometimes you have the printed value but not the format — a row in a supplier CSV, an
          OCR&apos;d scan, a screenshot from a colleague. A few visual and structural cues narrow it
          down quickly:
        </p>
        <ul>
          <li>
            <strong>Pure 12–14 digits</strong> → almost certainly a GTIN of some kind: UPC-A (12),
            EAN-13 (13) or ITF-14 (14). Validate the trailing check digit with the <Link to="/gtin-validator">GTIN Validator</Link>.
          </li>
          <li>
            <strong>13 digits starting with 978 or 979</strong> → ISBN-13.
          </li>
          <li>
            <strong>13 digits starting with 977</strong> → ISSN.
          </li>
          <li>
            <strong>
              Alphanumeric with bracketed groups like <code>(01)…(10)…</code>
            </strong> → GS1 Application Identifier syntax — either GS1-128 (1D) or GS1 Data Matrix (2D).
          </li>
          <li>
            <strong>
              A URL starting with <code>https://id.gs1.org/</code>
            </strong> or a brand&apos;s resolver — a GS1 Digital Link inside a QR or Data Matrix.
          </li>
          <li>
            <strong>Mixed-case ASCII with hyphens and a length over 14</strong> → most likely Code
            128 (or GS1-128 if it starts with FNC1).
          </li>
          <li>
            <strong>All uppercase, no lowercase</strong> → could be Code 39 if it&apos;s from an
            older industrial / defence workflow.
          </li>
        </ul>
        <p>
          For a GS1 element string like the one below, the GS1 decoder splits out each Application
          Identifier in one click:
        </p>
        <TryTool
          to="/gs1-digital-link-decoder"
          label="GS1 Decoder"
          description="Split a GS1 element string or Digital Link URL into its Application Identifiers."
          value="(01)05012345678900(10)ABC123(17)261231"
          cta="Detect the worked example"
        />
        <p>
          If the value turns out to be GS1-formatted, the <Link to="/gs1-digital-link-decoder">GS1 Digital Link Decoder</Link> parses the AIs (GTIN,
          batch, expiry, serial) into a structured breakdown.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="common-mistakes" title="Common Mistakes">
        <ul>
          <li>
            <strong>Treating QR as a synonym for &quot;any 2D barcode&quot;.</strong> Data Matrix is
            its industrial cousin and is usually the right answer for tight pharma / electronics
            packaging where QR is too large.
          </li>
          <li>
            <strong>Encoding a URL into a 1D barcode.</strong> Capacity collapses long before you
            fit it. Switch to a QR or use a GS1 Digital Link for retail.
          </li>
          <li>
            <strong>
              Mixing Code 39 and Code 128 in the same workflow without telling the parser.
            </strong> Both decode cleanly, but each has different escape rules for spaces and symbols.
          </li>
          <li>
            <strong>
              Printing ITF-14 at a module size below the GS1 minimum for the substrate.
            </strong> Cardboard ink bleed eats the fine bars; the symbol fails the print-grade check.
          </li>
          <li>
            <strong>Removing the 1D EAN the moment a 2D QR is added.</strong> Retail POS isn&apos;t
            fully Sunrise-ready yet; the till still needs the 1D barcode in most lanes through 2027.
          </li>
          <li>
            <strong>Reusing an EAN-8 allocation for a longer product line.</strong> EAN-8 is a
            separate GS1 allocation, not a truncated EAN-13. You have to apply for a new EAN-13
            allocation if you outgrow the short form.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="quick-decision" title="Quick Decision Guide">
        <p>
          When you only need the practical answer for a single label, four short rules cover most
          cases:
        </p>
        <ul>
          <li>
            <strong>Consumer pack</strong> → EAN-13 (or UPC-A in North America) + an optional QR
            Digital Link for Sunrise readiness.
          </li>
          <li>
            <strong>Logistics carton or pallet</strong> → ITF-14 on the case, GS1-128 on the pallet
            (SSCC plus AIs).
          </li>
          <li>
            <strong>Pharma / healthcare unit-dose</strong> → GS1 Data Matrix encoding GTIN + batch +
            expiry.
          </li>
          <li>
            <strong>Document, ticket or boarding pass</strong> → PDF417 for fixed-format US
            documents, Aztec for European rail / transport, QR for everything else.
          </li>
        </ul>
        <p>
          For round-trip QA after you&apos;ve generated a symbol, scan it back with the <Link to="/barcode-reader">Online Barcode Reader</Link>, validate any GTIN inside it with
          the <Link to="/gtin-validator">GTIN Validator</Link>, and parse any GS1 payload with the <Link to="/gs1-digital-link-decoder">GS1 Digital Link Decoder</Link>. For large batches,
          the <Link to="/batch-barcode-scanner">Batch Barcode Tools</Link> page validates a pasted
          list in one pass.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="how-many-types" title="How Many Types of Barcodes Are There?">
        <p>
          There is no official number of barcode types: ISO/IEC alone standardizes more than a dozen
          symbologies, and every one of them falls into one of three structural groups.
        </p>
        <ul>
          <li>
            <strong>Linear (1D) barcodes.</strong> A single row of bars and spaces read from side to side: EAN-13, EAN-8, UPC-A, UPC-E, ITF-14, Code 128, Code 39, Code 93, Codabar and the linear versions of GS1 DataBar.
          </li>
          <li>
            <strong>Stacked 2D barcodes.</strong> Several short rows of bars piled on top of each other: PDF417, MicroPDF417 and the stacked versions of GS1 DataBar.
          </li>
          <li>
            <strong>Matrix 2D barcodes.</strong> A pattern of modules read in both directions at once: QR Code, Micro QR Code, Data Matrix, Aztec, MaxiCode and DotCode.
          </li>
        </ul>
        <p>
          To tell the different types of barcodes apart, the <Link to="/barcode-type-detector">Barcode Type Detector</Link> checks a typed value against the GS1 formats and ISBN, and the <Link to="/barcode-reader">online barcode reader</Link> names the barcode format of a scanned symbol next to its value.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="types-by-industry" title="Barcode Types and Uses by Industry">
        <p>
          Each industry uses a few barcode types, because the scanners, printers and data rules
          along its supply chain have to agree.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Barcode types used in each industry and the data they carry.
            </caption>
            <thead>
              <tr>
                <th scope="col">Industry</th>
                <th scope="col">Main barcode types</th>
                <th scope="col">What they carry</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Retail checkout</th>
                <td>EAN-13, UPC-A, EAN-8, UPC-E, GS1 DataBar</td>
                <td>The GTIN of the consumer unit</td>
              </tr>
              <tr>
                <th scope="row">Warehousing and shipping</th>
                <td>GS1-128, ITF-14, Code 128</td>
                <td>The SSCC of a pallet, the GTIN-14 of a case, carrier tracking numbers</td>
              </tr>
              <tr>
                <th scope="row">Healthcare</th>
                <td>GS1 DataMatrix, GS1-128, Code 128</td>
                <td>GTIN, batch, expiry and serial number on medicines and devices; patient IDs on wristbands</td>
              </tr>
              <tr>
                <th scope="row">Publishing</th>
                <td>EAN-13 with a 2- or 5-digit add-on</td>
                <td>ISBN or ISSN, plus an issue number or a price</td>
              </tr>
              <tr>
                <th scope="row">Travel and tickets</th>
                <td>PDF417, Aztec, QR Code</td>
                <td>Boarding-pass and ticket data</td>
              </tr>
            </tbody>
          </table>
        </TableScroll>

        <h3>GS1 Barcode Types</h3>
        <p>
          GS1 barcode types are the symbologies that the GS1 General Specifications approve for
          carrying GS1 keys and Application Identifier data. They fall into five groups:
        </p>
        <ul>
          <li>EAN/UPC: EAN-13, EAN-8, UPC-A and UPC-E, the family retail checkouts are built around.</li>
          <li>ITF-14: the GTIN-14 of a carton.</li>
          <li>GS1-128: any GS1 key plus Application Identifier data on logistics labels.</li>
          <li>GS1 DataBar: seven versions, formerly called RSS, for small items and variable-measure fresh foods.</li>
          <li>GS1 2D symbols: GS1 DataMatrix, GS1 QR Code and GS1 DotCode, plus a QR Code or Data Matrix that holds a GS1 Digital Link URI.</li>
        </ul>

        <h3>UPC Barcode Types</h3>
        <p>
          There are two UPC barcode types, UPC-A and UPC-E, and either one can carry an optional
          add-on symbol printed to its right.
        </p>
        <ul>
          <li>
            <strong>UPC-A</strong> encodes a full 12-digit GTIN-12 and is the standard retail barcode in the US and Canada.
          </li>
          <li>
            <strong>UPC-E</strong> is a zero-suppressed form that fits some GTIN-12s starting with 0 into six encoded digits; scanners expand it back to 12.
          </li>
          <li>
            <strong>2-digit and 5-digit add-ons</strong> carry a magazine issue number or a book price, outside the GTIN.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="qr-barcode-types" title="QR Barcode Types">
        <p>
          QR Code, the best known of the 2D barcode types, has four variants: the obsolete Model 1,
          the Model 2 that almost every QR code printed today uses, Micro QR Code and the
          rectangular rMQR. Model 2 and Micro QR Code are defined in ISO/IEC 18004 and rMQR in
          ISO/IEC 23941.
        </p>
        <ul>
          <li>
            <strong>QR Code Model 2</strong> runs from version 1 (21 × 21 modules) to version 40 (177 × 177 modules), with a finder square in three corners and alignment patterns from version 2 up.
          </li>
          <li>
            <strong>Micro QR Code</strong> has a single finder square and four sizes, M1 to M4 (11 × 11 to 17 × 17 modules). The largest holds up to 35 digits.
          </li>
          <li>
            <strong>rMQR</strong> (rectangular Micro QR Code) is a long, narrow variant for strips of space such as a package edge or a cable label.
          </li>
        </ul>
        <p>
          Every Model 2 symbol also uses one of four error correction levels: L restores about 7% of
          the codewords, M about 15%, Q about 25% and H about 30%. A higher level means a larger
          symbol for the same data.
        </p>
        <p>
          People also say QR barcode types when they mean content: a web address, a <Link to="/wifi-qr-code-generator">Wi-Fi login</Link>, a <Link to="/vcard-qr-generator">vCard contact</Link> or a <Link to="/gs1-digital-link-generator">GS1 Digital Link</Link> all use the same symbol. GS1 QR Code differs, because it starts with FNC1 and holds a GS1 element string.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "Roughly a dozen formats cover almost every barcode in retail, logistics, healthcare and documents — the rest are industrial niches.",
          "1D formats encode a short identifier with a check digit; 2D formats encode a full payload with Reed–Solomon error correction.",
          "Match the format to the receiving end: retail POS wants EAN-13 / UPC-A, cardboard cases want ITF-14, pharma wants GS1 Data Matrix, screens want QR or Aztec.",
          "An unknown value's digit count, character set and bracketed structure narrow the symbology fast — the barcode reader confirms it from a scan or photo.",
          "Dual carriers (1D EAN + 2D QR with Digital Link) are the safe consumer-pack default through the Sunrise 2027 transition.",
        ]}
      />

      <GuideArticleSection id="references" title="References">
        <ul>
          <li>
            GS1 General Specifications — the canonical document covering UPC, EAN, GS1-128, GS1 Data
            Matrix, ITF-14 and the Application Identifier syntax. Published by <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              GS1
            </a>
            .
          </li>
          <li>
            GS1 EAN/UPC barcodes — overview of the retail 1D family: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
          <li>
            ISO/IEC 16022 (Data Matrix) and ISO/IEC 18004 (QR Code) — the international standards
            behind the two dominant 2D formats. Available from the <a href="https://www.iso.org/standards.html" rel="noopener noreferrer" target="_blank">
              ISO catalogue
            </a>
            .
          </li>
        </ul>
      </GuideArticleSection>

      <p>
        Ready to generate any of these symbologies? The <Link to="/barcode-generator">Barcode Generator</Link> covers the 1D family; <Link to="/qr-code-generator">QR Code</Link>, <Link to="/data-matrix-generator">Data Matrix</Link>, <Link to="/pdf417-generator">PDF417</Link> and <Link to="/aztec-code-generator">Aztec</Link> live on dedicated pages.
      </p>

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
